import {useIsDesktop} from '@eCommerce/frontend-legacy-shared-library-native';
import {DateTime, Interval} from 'luxon';
import {ChevronLeftIcon, ChevronRightIcon, View} from 'native-base';
import React, {useState} from 'react';
import {
  Calendar,
  CalendarList,
  CalendarProps,
  LocaleConfig,
} from 'react-native-calendars';
import {DateData, Direction, Theme} from 'react-native-calendars/src/types';
import {useTheme} from 'styled-components/native';

import {useFontFamily} from 'hooks/useFontFamily';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

import Day from './Day';
import config from './calendarLocale.config';

LocaleConfig.locales.en = config;
LocaleConfig.defaultLocale = 'en';

export interface DateSelectorProps {
  startDate: string | undefined;
  endDate: string | undefined;
  setEndDate: (date: string | undefined) => void;
  setStartDate: (date: string | undefined) => void;
  availableStartDate: string;
  availableEndDate: string;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  availableEndDate,
  availableStartDate,
}) => {
  const [currentDate, setCurrentDate] = useState('');
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const headerFontFamily = useFontFamily('heading', 700);
  const bodyFontFamily = useFontFamily('body');
  const now = DateTime.now().startOf('day');
  const availableStartDateTime =
    DateTime.fromISO(availableStartDate).startOf('day');
  const availableEndDateTime = DateTime.fromISO(availableEndDate);
  const minDate = availableStartDateTime < now ? now : availableStartDateTime;
  const currentDateTime = DateTime.fromISO(currentDate);
  const disableArrowLeft = currentDateTime.hasSame(minDate, 'month');
  const disableArrowRight = currentDateTime.hasSame(
    availableEndDateTime,
    'month'
  );
  const startDateTime = DateTime.fromISO(startDate || '');

  const getFutureScrollRange = () => {
    const start = startDate ? startDateTime : minDate;
    return Math.floor(
      availableEndDateTime.endOf('month').diff(start.startOf('month'), 'months')
        .months
    );
  };

  const getPastScrollRange = () => {
    if (!startDate) {
      return 0;
    }
    return Math.floor(
      startDateTime.startOf('month').diff(minDate.startOf('month'), 'months')
        .months
    );
  };

  const getMarkedDates = () => {
    if (!startDate || !endDate) {
      return;
    }
    let markedDates = {};
    let beginning = DateTime.fromISO(startDate).plus({days: 1});
    const ending = DateTime.fromISO(endDate);
    while (beginning < ending) {
      markedDates = {...markedDates, [beginning.toISODate()]: {selected: true}};
      beginning = beginning.plus({days: 1});
    }
    return markedDates;
  };

  const markedDates = {
    [startDate ?? '']: {
      selected: true,
      startingDay: true,
    },
    [endDate ?? '']: {
      selected: true,
      endingDay: true,
    },
    ...getMarkedDates(),
  };
  const {trackAdobeEvent} = useTrackEvent();
  const onDayPress = (selectedDate: DateData) => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'rewards filter'},
      rewards: {category: 'DATES'},
    });
    if (!startDate) {
      return setStartDate(selectedDate.dateString);
    }
    if (startDate && endDate) {
      const interval = Interval.fromDateTimes(
        DateTime.fromISO(availableStartDate),
        DateTime.fromISO(availableEndDate).plus({days: 1})
      );

      const isOutOfRange = !interval.contains(
        DateTime.fromISO(selectedDate.dateString)
      );

      if (isOutOfRange) {
        setStartDate(undefined);
      } else {
        setStartDate(selectedDate.dateString);
      }
      return setEndDate(undefined);
    }
    if (selectedDate.dateString <= startDate) {
      return setStartDate(undefined);
    }
    setEndDate(selectedDate.dateString);
  };

  const renderArrow = (direction: Direction) => {
    switch (direction) {
      case 'right':
        return (
          <ChevronRightIcon
            color="tuna"
            opacity={disableArrowRight ? 0.5 : 1}
          />
        );
      case 'left':
        return (
          <ChevronLeftIcon color="tuna" opacity={disableArrowLeft ? 0.5 : 1} />
        );

      default:
        return undefined;
    }
  };

  const calendarTheme: Theme = {
    monthTextColor: theme.colors.tuna,
    textMonthFontFamily: headerFontFamily,
    textMonthFontWeight: '700',
    textMonthFontSize: 15,
    textDayHeaderFontFamily: bodyFontFamily,
    textDayHeaderFontWeight: '600',
    textSectionTitleColor: theme.colors.tuna,
    textDayHeaderFontSize: 9,
    // @ts-expect-error this is the only way these style changes take effect but the type error can't be fixed
    'stylesheet.calendar.header': {
      header: {
        padding: 0,
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 6,
        alignItems: 'center',
      },
    },
    'stylesheet.calendar.main': {
      week: {
        marginTop: 1,
        marginBottom: 1,
        flexDirection: 'row',
        justifyContent: 'space-around',
      },
    },
  };

  const calendarConfig: CalendarProps = {
    markedDates,
    theme: calendarTheme,
    onDayPress,
    renderArrow,
    markingType: 'period',
    minDate: minDate.toJSDate(),
    maxDate: availableEndDateTime.toJSDate(),
    disableAllTouchEventsForDisabledDays: true,
    // @ts-expect-error wrong typing
    current: startDate || minDate.toJSDate(),
    disableArrowLeft: disableArrowLeft,
    disableArrowRight: disableArrowRight,
    // @ts-expect-error wrong typing
    onMonthChange: date => setCurrentDate(date.dateString),
    dayComponent: props => (
      <Day
        {...props}
        onDayPress={onDayPress}
        availableStartDate={availableStartDate}
        availableEndDate={availableEndDate}
      />
    ),
  };

  return isDesktop ? (
    <View pb={4}>
      <Calendar testID="date-selector-calendar" {...calendarConfig} />
    </View>
  ) : (
    <CalendarList
      testID="date-selector-calendar-list"
      {...calendarConfig}
      pastScrollRange={getPastScrollRange()}
      futureScrollRange={getFutureScrollRange()}
    />
  );
};

export default DateSelector;
